import useTranslation from "../../client/hooks/useTranslation";
import { useLiteData } from "../data/LiteDataProvider";
import { closedOnly, net } from "../lib/stats";
import type { LiteTrade } from "../lib/types";
import { toneColor } from "./ui";

/** Résultat par jour de semaine et par heure d'entrée. */
export function HourHeatmap({ trades }: { trades: LiteTrade[] }) {
  const t = useTranslation().lite;
  const { fmt } = useLiteData();
  const days = t.common.weekdaysShort.slice(0, 5);
  const cells = new Map<string, { pnl: number; n: number }>();
  const hours = new Set<number>();
  for (const tr of closedOnly(trades)) {
    const d = new Date(tr.openedAt);
    const dow = (d.getDay() + 6) % 7;
    if (dow > 4) continue;
    const h = d.getHours();
    hours.add(h);
    const k = `${dow}-${h}`;
    const c = cells.get(k) ?? { pnl: 0, n: 0 };
    c.pnl += net(tr); c.n++;
    cells.set(k, c);
  }
  const hs = [...hours].sort((a, b) => a - b);
  const maxAbs = Math.max(1, ...[...cells.values()].map(c => Math.abs(c.pnl)));

  return (
    <div className="overflow-x-auto">
      <div className="grid gap-1 min-w-[320px]" style={{ gridTemplateColumns: `40px repeat(${hs.length}, minmax(0, 1fr))` }}>
        <div />
        {hs.map(h => <div key={h} className="text-[11px] text-muted-foreground/70 text-center">{t.common.hour(h)}</div>)}
        {days.map((day, di) => [
          <div key={day} className="text-[11px] text-muted-foreground/70 flex items-center">{day}</div>,
          ...hs.map(h => {
            const c = cells.get(`${di}-${h}`);
            const alpha = c ? 0.12 + 0.6 * Math.min(1, Math.abs(c.pnl) / maxAbs) : 0;
            return (
              <div
                key={`${di}-${h}`}
                title={c ? t.charts.heatmapCell(day, t.common.hour(h), fmt.money(c.pnl), c.n) : t.charts.heatmapEmpty(day, t.common.hour(h))}
                className="h-9 rounded-md grid place-items-center text-[10px] font-mono text-foreground/80"
                style={{ background: c ? toneColor(c.pnl >= 0 ? "gain" : "loss", alpha) : "hsl(var(--muted) / .5)" }}
              >
                {c ? c.n : ""}
              </div>
            );
          }),
        ])}
      </div>
      <div className="flex items-center gap-4 mt-3 text-[11px] text-muted-foreground/70">
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-pnlPositive/60" /> {t.charts.winners}</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-pnlNegative/60" /> {t.charts.losers}</span>
        <span>{t.charts.tradeCountHint}</span>
      </div>
    </div>
  );
}
